import { fromIso9075DateString } from 'utils/dates';
import {
  REWARD_TYPE,
  UBER_EVENT_TYPE,
  UBER_REDEEMED_STATUS,
  getRewardType
} from 'utils/rewards';

const shouldShowRedeemButton = rewardData => {
  return (
    rewardData.offerType !== REWARD_TYPE.BATTLE_SLOTS &&
    rewardData.offerType !== REWARD_TYPE.WCFOOD_CREDIT &&
    rewardData.offerType !== REWARD_TYPE.SOFT_OFFER_CMP
  );
};

const getPartnerRewardsPrizeCodeLabel = type => {
  if (type === REWARD_TYPE.UBER) return 'Voucher Code';
};

export const formatPlsRewards = rewardsData => {
  const rewards = rewardsData.map(rewardData => {
    const reward = {
      rewardId: rewardData.prizeAssignmentId,
      name: rewardData.plsPrize.name,
      type: getRewardType(rewardData),
      quantity: rewardData.plsPrize.valueInCents,
      redeemed: rewardData.usedValueInCents,
      description: rewardData.plsPrize.description,
      startDateDisplay: rewardData.validFromDateTime,
      endDateDisplay: rewardData.validToDateTime,
      startDate: fromIso9075DateString(rewardData.validFromDateTimeUTC),
      endDate: fromIso9075DateString(rewardData.validToDateTimeUTC),
      redeemedDate: fromIso9075DateString(rewardData.redeemedDateUTC),
      shouldShowRedeemButton: false
    };

    // Specific properties for PLS Rewards
    reward.prizeType = rewardData.plsPrize.prizeType;
    reward.dailyValidFromTime = rewardData.dailyValidFromTime;
    reward.dailyValidToTime = rewardData.dailyValidToTime;
    reward.blackoutDates = rewardData.blackoutDates;
    reward.isPlsReward = true;
    reward.allowsPartialRedemption =
      rewardData.plsPrize.allowsPartialRedemption;
    reward.totalValue = rewardData.plsPrize.value;
    reward.remainingValue = rewardData.remainingValue;
    reward.usedValue = rewardData.usedValue;
    reward.category = rewardData.plsPrize.category;

    return reward;
  });
  rewards.sort((lhs, rhs) => lhs.endDate - rhs.endDate);

  return rewards;
};

export const formatOnlineOffers = onlineOffersData => {
  const onlineOffers = onlineOffersData.map(rewardData => {
    const reward = {
      rewardId: rewardData.offerUuid,
      name: rewardData.name,
      type: rewardData.offerType,
      quantity: rewardData.amount,
      redeemed: rewardData.redeemedAmount || 0,
      description: rewardData.description,
      isNew: rewardData.isNew,
      startDateDisplay: rewardData.startDate,
      endDateDisplay: rewardData.endDate,
      startDate: fromIso9075DateString(rewardData.startDateUTC),
      endDate: fromIso9075DateString(rewardData.endDateUTC),
      redeemedDate: fromIso9075DateString(rewardData.redeemedDateUTC),
      shouldShowRedeemButton: shouldShowRedeemButton(rewardData)
    };

    // Specific properties for Online Offers
    reward.onlineOfferUuid = rewardData.offerUuid;
    reward.isSoftOffer = true;

    return reward;
  });
  onlineOffers.sort((lhs, rhs) => lhs.endDate - rhs.endDate);

  return onlineOffers;
};

export const formatPatronRewards = rewardsData => {
  const rewards = rewardsData.map(rewardData => {
    const reward = {
      rewardId: rewardData.prizeCode,
      name: rewardData.prizeName,
      type: getRewardType(rewardData),
      quantity: rewardData.maxPrizesPerPeriod,
      redeemed: rewardData.redeemed || 0,
      description: rewardData.prize?.description ?? rewardData.prizeName,
      isNew: rewardData.isNewOffer,
      startDateDisplay: rewardData.startDate,
      endDateDisplay: rewardData.endDate,
      startDate: fromIso9075DateString(rewardData.startDateUTC),
      endDate: fromIso9075DateString(rewardData.endDateUTC),
      redeemedDate: fromIso9075DateString(rewardData.redeemedDateUTC),
      shouldShowRedeemButton: false
    };

    // Specific properties for Patron Rewards
    reward.onlineOfferUuid = rewardData.onlineOfferUuid;
    reward.isSoftOffer = rewardData.isSoftOffer;
    reward.prizeType = rewardData.prizeType;
    reward.url = rewardData.prize?.url || '';
    reward.category = rewardData.prize?.category || '';

    return reward;
  });
  rewards.sort((lhs, rhs) => lhs.endDate - rhs.endDate);

  return rewards;
};

export const formatPartnerRewards = rewardsData => {
  const rewards = rewardsData.map(rewardData => {
    const reward = {
      rewardId: rewardData.redeemCode,
      name: rewardData.prize.name,
      type: rewardData.prize.subCategory,
      quantity: 1,
      redeemed: UBER_REDEEMED_STATUS.includes(rewardData.event.eventType)
        ? 1
        : 0,
      description: rewardData.prize.description,
      startDateDisplay: rewardData.startDate,
      endDateDisplay: rewardData.endDate,
      startDate: fromIso9075DateString(rewardData.startDateUTC),
      endDate: fromIso9075DateString(rewardData.endDateUTC),
      eventDate: rewardData.event.eventDate,
      redeemedDate: fromIso9075DateString(rewardData.redeemedDateUTC),
      shouldShowRedeemButton: false
    };

    // Specific properties for Partner Rewards
    reward.prizeType = rewardData.prize.subCategory;
    reward.isUberReward = rewardData.prize.subCategory === REWARD_TYPE.UBER;
    reward.prizeCodeLabel = getPartnerRewardsPrizeCodeLabel(
      rewardData.prize.subCategory
    );
    reward.canceled = rewardData.event.eventType === UBER_EVENT_TYPE.CANCELED;
    reward.claimed = rewardData.event.eventType === UBER_EVENT_TYPE.CLAIMED;
    reward.expired = rewardData.event.eventType === UBER_EVENT_TYPE.EXPIRED;
    reward.location = rewardData.prize.locations[0];
    reward.amount = rewardData.amount;
    reward.url = rewardData.prize?.url || '';
    reward.redeemLink = rewardData.redeemLink || '';

    return reward;
  });
  rewards.sort((lhs, rhs) => lhs.endDate - rhs.endDate);

  return rewards;
};
